import { useCallback, useEffect, useState } from 'react';
import { Calculator, ClipboardList, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { DailySummaryGeneratePanel } from '../components/daily-summary/DailySummaryGeneratePanel';
import { DailySummaryHistory } from '../components/daily-summary/DailySummaryHistory';

const SHIFT_PRESETS = [
  { label: 'Full Day', start: '00:00', end: '23:59' },
  { label: 'Morning (6AM - 2PM)', start: '06:00', end: '14:00' },
  { label: 'Afternoon (2PM - 10PM)', start: '14:00', end: '22:00' },
  { label: 'Night (10PM - 6AM)', start: '22:00', end: '06:00' },
  { label: 'Custom', start: '', end: '' },
];

const initialForm = {
  date: new Date().toISOString().split('T')[0], shiftPreset: 'Full Day', shiftStart: '00:00', shiftEnd: '23:59',
  declaredCash: '', declaredMobileMoney: '', declaredCard: '', declaredOther: '', startupCash: '', cashLeftBehind: '', notes: '',
};

export default function DailySummaryPage() {
  const [activeView, setActiveView] = useState('generate');
  const [form, setForm] = useState(initialForm);
  const [generating, setGenerating] = useState(false);
  const [generatedReport, setGeneratedReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPages, setHistoryPages] = useState(1);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get('/daily-summary', { params: { page: historyPage, limit: 15 } });
      setReports(data.reports || []);
      setHistoryPages(data.pages || 1);
    } catch {
      toast.error('Failed to load report history');
    } finally {
      setHistoryLoading(false);
    }
  }, [historyPage]);

  useEffect(() => {
    if (activeView === 'history') fetchHistory();
  }, [activeView, fetchHistory]);

  const updateForm = (patch) => setForm((current) => ({ ...current, ...patch }));
  const handleShiftPreset = (label) => {
    const preset = SHIFT_PRESETS.find((item) => item.label === label);
    updateForm(label === 'Custom' ? { shiftPreset: label } : { shiftPreset: label, shiftStart: preset.start, shiftEnd: preset.end });
  };
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/daily-summary/generate', {
        date: form.date, shift_start: form.shiftStart, shift_end: form.shiftEnd,
        shift_label: form.shiftPreset === 'Custom' ? `${form.shiftStart} - ${form.shiftEnd}` : form.shiftPreset,
        startup_cash: parseFloat(form.startupCash) || 0, declared_cash: parseFloat(form.declaredCash) || 0,
        cash_left_behind: parseFloat(form.cashLeftBehind) || 0, declared_mobile_money: parseFloat(form.declaredMobileMoney) || 0,
        declared_card: parseFloat(form.declaredCard) || 0, declared_other: parseFloat(form.declaredOther) || 0, notes: form.notes,
      });
      setGeneratedReport(data);
      toast.success('Daily summary generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="daily-summary-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}><ClipboardList className="h-6 w-6 text-primary" /> Daily Sales Summary</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Shift-based cash reconciliation report</p>
        </div>
        <div className="flex gap-2">
          <Button variant={activeView === 'generate' ? 'default' : 'outline'} onClick={() => setActiveView('generate')} className="gap-1.5" data-testid="tab-generate"><Calculator className="h-4 w-4" /> Generate</Button>
          <Button variant={activeView === 'history' ? 'default' : 'outline'} onClick={() => setActiveView('history')} className="gap-1.5" data-testid="tab-history"><Clock className="h-4 w-4" /> History</Button>
        </div>
      </div>
      {activeView === 'generate' ? (
        <DailySummaryGeneratePanel form={form} presets={SHIFT_PRESETS} onFormChange={updateForm} onPresetChange={handleShiftPreset} onGenerate={handleGenerate} generating={generating} report={generatedReport} />
      ) : (
        <DailySummaryHistory reports={reports} loading={historyLoading} page={historyPage} pages={historyPages} onPageChange={setHistoryPage} />
      )}
    </div>
  );
}